// ============================================================================
// Line Official Account Adapter — public surface + registration helper.
//
// This file is the one the rest of the module imports. At module-load time,
// `registerLineOaAdapter()` wires the implementation into BOTH registries:
//
//   1. `webhook-adapters` (slug-keyed) — powers the public ingress route
//      `POST /api/v1/connectors/webhooks/line-oa/:instanceId`.
//   2. `capability-registry` (typeId-keyed) — lets any cross-module caller
//      reach Line via `call(instanceId, 'messaging', 'sendMessage', ...)`.
//
// Matching up the keys: the `003_line_oa_seed.ts` migration sets
// `connectors_types.id = connectors_types.slug = 'line-oa'`, so the same
// string addresses the row from every angle (instance.type_id, URL path,
// capability register, webhook register).
// ============================================================================

import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import { buildLineOaMessagingMethods } from './capability-methods'
import { lineOaWebhookAdapter } from './webhook'

/** Canonical type id / slug. MUST match the `connectors_types` seed row. */
export const LINE_OA_TYPE_SLUG = 'line-oa'

export interface RegisterLineOaOpts {
  /** Injectable fetch — tests swap it out. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
}

/**
 * Register the Line OA adapter in both the webhook and capability registries.
 * Idempotent: re-calling just overwrites the prior registration (matches the
 * semantics of the underlying registries). Safe to call at module-load +
 * test-reset points without special-casing.
 */
export function registerLineOaAdapter(opts: RegisterLineOaOpts = {}): void {
  registerWebhookAdapter(LINE_OA_TYPE_SLUG, lineOaWebhookAdapter)
  registerCapability(
    LINE_OA_TYPE_SLUG,
    'messaging',
    buildLineOaMessagingMethods(opts),
  )
}

// ============================================================================
// Barrel — re-export the pieces consumers might reach for directly.
// ============================================================================

export { lineOaWebhookAdapter, verifyLineSignature, parseLineWebhook } from './webhook'

export {
  buildLineOaMessagingMethods,
  type LineOaMessagingMethodsDeps,
  type LineSendMessageArgs,
} from './capability-methods'

export {
  // Constants
  LINE_LOGIN_AUTHORIZE_URL,
  LINE_LOGIN_TOKEN_URL,
  LINE_LOGIN_DEFAULT_SCOPES,
  LINE_API_BASE as LINE_OAUTH_API_BASE,
  // Functions
  validateChannelAccessToken,
  buildAuthorizeUrl,
  exchangeCode,
  refreshLineToken,
  redactLineSecrets,
  // Errors + types
  LineOaOAuthError,
  type LineChannelInfo,
  type LineOaAuthorizeInput,
  type LineOaAuthorizeResult,
  type LineExchangeCodeInput,
  type LineExchangeCodeResult,
  type RefreshLineTokenInput,
  type LineOaOAuthErrorCode,
} from './oauth'

export {
  sendLineReply,
  sendLinePush,
  buildLineTextMessage,
  buildLineQuickReply,
  buildMessagesFromAttachments,
  LINE_API_BASE,
  type LineMessage,
  type LineTextMessage,
  type LineImageMessage,
  type LineVideoMessage,
  type LineAudioMessage,
  type LineLocationMessage,
  type LineStickerMessage,
  type LineFlexMessage,
  type LineRawMessage,
  type LineQuickReply,
  type LineQuickReplyAction,
} from './send'

export {
  buildFlexMessage,
  buildFlexCarousel,
  type FlexBubble,
  type FlexCarousel,
} from './flex'

export { getLineUserProfile } from './profile'

export {
  LineOaError,
  LineOaSignatureError,
  classifyLineError,
  parseRetryAfterMs,
  sanitizeLineErrorMessage,
  type LineOaErrorCode,
  type LineOaErrorDetails,
  type LineSendResponse,
  type LineBotInfo,
  type LineUserProfileRaw,
  type LineErrorEnvelope,
} from './types'
